import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Book, Code, Server, Zap } from 'lucide-react';
import TechStack from '../components/landing/TechStack';
import CodeSnippets from '../components/landing/CodeSnippets';
import APIReference from '../components/landing/APIReference';

const DevelopersPage = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', name: 'Overview', icon: Book },
    { id: 'api', name: 'API Reference', icon: Code },
    { id: 'architecture', name: 'Architecture', icon: Server },
    { id: 'quickstart', name: 'Quick Start', icon: Zap },
  ];

  return (
    <div className="f500-page min-h-screen bg-white" data-testid="developers-page">
      {/* Header */}
      <div className="bg-white border-b border-[var(--f500-line)] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-[var(--f500-ink-2)] hover:text-[var(--f500-ink)] transition-colors text-sm"
              data-testid="back-to-home"
              style={{ fontFamily: 'var(--sf-text)' }}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
            <div className="flex items-center gap-3">
              <span className="font-bold text-[var(--f500-ink)]" style={{ fontFamily: 'var(--sf-display)', letterSpacing: '-0.02em' }}>
                iStudyPro
              </span>
              <span className="f500-badge f500-badge-blue">
                Developers
              </span>
            </div>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <nav className="flex gap-0 overflow-x-auto -mb-px">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                    isActive
                      ? 'text-[var(--f500-ink)] border-[var(--f500-blue)]'
                      : 'text-[var(--f500-ink-2)] border-transparent hover:text-[var(--f500-ink)]'
                  }`}
                  style={{ fontFamily: 'var(--sf-text)' }}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.75} />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Page Title */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Developer Documentation
          </h1>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Everything you need to integrate iStudyPro into your application.
          </p>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          {/* Quick Links */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <button
              onClick={() => setActiveTab('api')}
              className="p-6 bg-slate-800/50 rounded-2xl border border-white/10 hover:border-purple-500/50 transition-colors text-left group"
            >
              <Code className="w-8 h-8 text-purple-400 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">
                API Reference
              </h3>
              <p className="text-white/60 text-sm">
                Complete REST API documentation with request/response examples.
              </p>
            </button>
            
            <button
              onClick={() => setActiveTab('architecture')}
              className="p-6 bg-slate-800/50 rounded-2xl border border-white/10 hover:border-blue-500/50 transition-colors text-left group"
            >
              <Server className="w-8 h-8 text-blue-400 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">
                Architecture
              </h3>
              <p className="text-white/60 text-sm">
                System design, database schemas, and infrastructure overview.
              </p>
            </button>
            
            <button
              onClick={() => setActiveTab('quickstart')}
              className="p-6 bg-slate-800/50 rounded-2xl border border-white/10 hover:border-green-500/50 transition-colors text-left group"
            >
              <Zap className="w-8 h-8 text-green-400 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2 group-hover:text-green-400 transition-colors">
                Quick Start
              </h3>
              <p className="text-white/60 text-sm">
                Get up and running in under 5 minutes with our SDK.
              </p>
            </button>
          </div>
          
          {/* Features Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white">Core Features</h2>
              <ul className="space-y-4">
                {[
                  { title: 'AI Transcription', desc: 'Whisper-powered audio to text conversion' },
                  { title: 'Smart Summaries', desc: 'Cornell Notes format with key points extraction' },
                  { title: 'Study Buddy Chat', desc: 'Gemini-powered conversational AI tutor' },
                  { title: 'Learning Bytes', desc: 'Auto-generated flashcards and quizzes' },
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-purple-400 text-xs">✓</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium">{feature.title}</h4>
                      <p className="text-white/60 text-sm">{feature.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white">Payment Integration</h2>
              <ul className="space-y-4">
                {[
                  { title: 'Paystack Gateway', desc: 'Secure payments with Mobile Money support' },
                  { title: 'Multi-Currency', desc: 'USD, EUR, GBP, NGN, GHS, KES, ZAR' },
                  { title: 'Subscription Management', desc: 'Monthly and yearly billing cycles' },
                  { title: 'Webhook Events', desc: 'Real-time payment status updates' },
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-green-400 text-xs">✓</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium">{feature.title}</h4>
                      <p className="text-white/60 text-sm">{feature.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'api' && <APIReference />}
      
      {activeTab === 'architecture' && (
        <>
          <TechStack />
          <CodeSnippets />
        </>
      )}
      
      {activeTab === 'quickstart' && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="space-y-8">
            {/* Step 1 */}
            <div className="p-6 bg-slate-800/50 rounded-2xl border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold">1</span>
                <h3 className="text-xl font-bold text-white">Get Your API Key</h3>
              </div>
              <p className="text-white/70 mb-4">
                Sign up for a Pro account to get API access. Your API key will be available in Settings.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
              >
                Sign Up for Pro
              </Link>
            </div>
            
            {/* Step 2 - Authentication */}
            <div className="p-6 bg-slate-800/50 rounded-2xl border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold">2</span>
                <h3 className="text-xl font-bold text-white">Authenticate Your Requests</h3>
              </div>
              <p className="text-white/70 mb-4">
                Login to get your JWT token, then include it in all subsequent requests:
              </p>
              <div className="bg-slate-900 rounded-lg overflow-hidden">
                <div className="px-4 py-2 bg-slate-800/50 border-b border-white/10 flex items-center gap-2">
                  <span className="text-xs text-white/50">JavaScript</span>
                </div>
                <pre className="p-4 text-sm text-white/80 overflow-x-auto">
{`// Step 1: Login to get token
const response = await fetch('{API_BASE_URL}/api/auth/login', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    email: 'your@email.com',
    password: 'your-password'
  })
});

const { access_token, user } = await response.json();

// Step 2: Use token in subsequent requests
const headers = {
  'Authorization': \`Bearer \${access_token}\`,
  'Content-Type': 'application/json'
};`}
                </pre>
              </div>
            </div>
            
            {/* Step 3 - Study Buddy Chat */}
            <div className="p-6 bg-slate-800/50 rounded-2xl border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold">3</span>
                <h3 className="text-xl font-bold text-white">Chat with Study Buddy AI</h3>
              </div>
              <p className="text-white/70 mb-4">
                Use the Study Buddy API for AI-powered tutoring conversations:
              </p>
              <div className="space-y-4">
                <div className="bg-slate-900 rounded-lg overflow-hidden">
                  <div className="px-4 py-2 bg-slate-800/50 border-b border-white/10 flex items-center gap-2">
                    <span className="text-xs text-white/50">Python</span>
                  </div>
                  <pre className="p-4 text-sm text-white/80 overflow-x-auto">
{`import requests

API_URL = "{API_BASE_URL}/api"

# Chat with Study Buddy
response = requests.post(
    f"{API_URL}/study-buddy/chat",
    headers={"Authorization": f"Bearer {token}"},
    json={
        "messages": [
            {"role": "user", "content": "Explain the Pythagorean theorem"}
        ]
    }
)

answer = response.json()
print(answer["content"])`}
                  </pre>
                </div>
                <div className="bg-slate-900 rounded-lg overflow-hidden">
                  <div className="px-4 py-2 bg-slate-800/50 border-b border-white/10 flex items-center gap-2">
                    <span className="text-xs text-white/50">cURL</span>
                  </div>
                  <pre className="p-4 text-sm text-white/80 overflow-x-auto">
{`curl -X POST {API_BASE_URL}/api/study-buddy/chat \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"messages": [{"role": "user", "content": "Explain photosynthesis"}]}'`}
                  </pre>
                </div>
              </div>
            </div>

            {/* Step 4 - Generate Flashcards */}
            <div className="p-6 bg-slate-800/50 rounded-2xl border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold">4</span>
                <h3 className="text-xl font-bold text-white">Generate Flashcards & Quizzes</h3>
              </div>
              <p className="text-white/70 mb-4">
                Automatically generate study materials from lecture content:
              </p>
              <div className="bg-slate-900 rounded-lg overflow-hidden">
                <div className="px-4 py-2 bg-slate-800/50 border-b border-white/10 flex items-center gap-2">
                  <span className="text-xs text-white/50">JavaScript</span>
                </div>
                <pre className="p-4 text-sm text-white/80 overflow-x-auto">
{`// Generate flashcards from a lecture
const lectureId = 'your-lecture-id';
const response = await fetch(
  \`{API_BASE_URL}/api/learning-bytes/generate/\${lectureId}?num_flashcards=10&num_quiz_questions=5\`,
  {
    method: 'POST',
    headers: { 'Authorization': \`Bearer \${token}\` }
  }
);

const { flashcards, quiz_questions } = await response.json();

// Each flashcard has: { front: "Question", back: "Answer" }
// Each quiz has: { question, options, correct_answer }`}
                </pre>
              </div>
            </div>

            {/* Step 5 - Transcribe Audio */}
            <div className="p-6 bg-slate-800/50 rounded-2xl border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold">5</span>
                <h3 className="text-xl font-bold text-white">Transcribe Audio with Whisper AI</h3>
              </div>
              <p className="text-white/70 mb-4">
                Upload lecture audio to get AI-powered transcriptions:
              </p>
              <div className="bg-slate-900 rounded-lg overflow-hidden">
                <div className="px-4 py-2 bg-slate-800/50 border-b border-white/10 flex items-center gap-2">
                  <span className="text-xs text-white/50">Python</span>
                </div>
                <pre className="p-4 text-sm text-white/80 overflow-x-auto">
{`import requests

# Transcribe an audio file
with open('lecture.mp3', 'rb') as audio_file:
    response = requests.post(
        f"{API_URL}/transcribe",
        headers={"Authorization": f"Bearer {token}"},
        files={"file": audio_file}
    )

transcript = response.json()["transcript"]
print(transcript)`}
                </pre>
              </div>
            </div>

            {/* Rate Limits */}
            <div className="p-6 bg-amber-500/10 rounded-2xl border border-amber-500/30">
              <h3 className="text-xl font-bold text-amber-400 mb-4">⚠️ Rate Limits</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <h4 className="font-bold text-white mb-2">Free Plan</h4>
                  <ul className="text-sm text-white/70 space-y-1">
                    <li>• 10 AI requests/day</li>
                    <li>• 5 lectures max</li>
                    <li>• 3 subjects max</li>
                  </ul>
                </div>
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <h4 className="font-bold text-white mb-2">Student Plan</h4>
                  <ul className="text-sm text-white/70 space-y-1">
                    <li>• 100 AI requests/day</li>
                    <li>• 50 lectures max</li>
                    <li>• 10 subjects max</li>
                  </ul>
                </div>
                <div className="bg-slate-800/50 rounded-xl p-4 border border-purple-500/30">
                  <h4 className="font-bold text-purple-400 mb-2">Pro Plan</h4>
                  <ul className="text-sm text-white/70 space-y-1">
                    <li>• Unlimited AI requests</li>
                    <li>• Unlimited lectures</li>
                    <li>• Unlimited subjects</li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Step 6 - Explore More */}
            <div className="p-6 bg-slate-800/50 rounded-2xl border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold">✓</span>
                <h3 className="text-xl font-bold text-white">Explore the Full API</h3>
              </div>
              <p className="text-white/70 mb-4">
                Check out the complete API reference for all endpoints including Study Groups, Gamification, Payments, and more:
              </p>
              <button
                onClick={() => setActiveTab('api')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
              >
                <Code className="w-4 h-4" />
                View API Reference
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer CTA */}
      <div className="bg-slate-900 border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">Ready to build?</h3>
          <p className="text-white/60 mb-6">
            Join thousands of students using iStudyPro
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-semibold rounded-full hover:opacity-90 transition-opacity"
            data-testid="get-started-cta"
          >
            Get Started
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DevelopersPage;
